import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import GatewayCard from './components/GatewayCard';
import { paymentGatewaysService } from '../../services/supabaseService';

const PaymentGatewayConfiguration = () => {
  const [loading, setLoading] = useState(true);
  const [gateways, setGateways] = useState([]);

  useEffect(() => {
    const fetchGateways = async () => {
      setLoading(true);
      try {
        const { data } = await paymentGatewaysService?.getAll();
        if (data) {
          setGateways(data);
        }
      } catch (error) {
        console.error('Error fetching payment gateways:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGateways();
  }, []);

  // Real-time subscription
  useEffect(() => {
    const unsubscribe = paymentGatewaysService?.subscribe((payload) => {
      if (payload?.eventType === 'INSERT' && payload?.new) {
        setGateways(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE' && payload?.new) {
        setGateways(prev => prev?.map(g => g?.id === payload?.new?.id ? payload?.new : g));
      } else if (payload?.eventType === 'DELETE' && payload?.old) {
        setGateways(prev => prev?.filter(g => g?.id !== payload?.old?.id));
      }
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const handleToggleStatus = async (gatewayId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const { error } = await paymentGatewaysService?.updateStatus(gatewayId, newStatus);
    
    if (!error) {
      setGateways(prev => prev?.map(g => 
        g?.id === gatewayId ? { ...g, status: newStatus } : g
      ));
    }
  };

  const handleConfigure = (gateway) => {
    console.log('Configure gateway:', gateway?.name);
    // Configuration logic would go here
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Payment Gateways - PaymentNexus</title>
        </Helmet>
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading payment gateways...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Payment Gateways - PaymentNexus</title>
        <meta name="description" content="Configure and manage payment providers" />
      </Helmet>
      <Header />
      
      <main className="container mx-auto px-4 py-6 md:py-8 lg:py-10">
        {/* Page Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
            Payment Gateways
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Configure and manage payment providers
          </p>
        </div>

        {/* Gateway Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {gateways?.map((gateway) => (
            <GatewayCard
              key={gateway?.id}
              gateway={gateway}
              onToggleStatus={handleToggleStatus}
              onConfigure={handleConfigure}
            />
          ))}
        </div>

        {gateways?.length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No payment gateways configured yet.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default PaymentGatewayConfiguration;